import { ResponsiveLine, LineProps } from "@nivo/line";
import { COLORS } from "styles/colors";
import { useAppSelector } from "redux/hooks";
import React from "react";
import { BoxLegendSvg } from "@nivo/legends";
import { JSX } from "react/jsx-runtime";
import { getChartColorSchema } from "utils/functions";
import * as mediaQuery from "components/MediaQuery";
import LineChartTooltip from "./LineChartTooltip";
import styled from "styled-components";
import { calcRem } from "styles/theme";
import { getFormatFilter } from "utils/ReportUtil";

export interface dwLineProps extends LineProps {
  fixedMax?: number;
  height?: number;
  intervalValue?: string;
}

const BarLegend = ({ data, height, legends, width }: any) => (
  <React.Fragment>
    {legends.map((legend: any, index: number) => (
      <BoxLegendSvg
        {...legend}
        containerHeight={height}
        containerWidth={width}
        key={index}
      ></BoxLegendSvg>
    ))}
  </React.Fragment>
);

export default function LineInterpolationChart(
  props: dwLineProps
): JSX.Element {
  const theme = useAppSelector((state) => state.theme.theme);
  const color = theme === "light" ? COLORS.BLACK : COLORS.WHITE;
  const isMobile = mediaQuery.useIsMobile();

  return (
    <Wrapper
      style={{
        height: (props.height || 0) + (isMobile ? props.data.length * 30 : 0),
      }}
    >
      <ResponsiveLine
        {...props}
        margin={{
          top: isMobile ? props.data.length * 30 : 30,
          right: isMobile ? 10 : 0,
          bottom: isMobile ? 0 : 60,
          left: isMobile ? 30 : 60,
        }}
        xScale={{ type: "point" }}
        yScale={{
          type: "linear",
          min: 0,
          max: props.fixedMax ? props.fixedMax : "auto",
          // stacked: true,
          reverse: false,
        }}
        curve="monotoneX"
        // yFormat=" >-.2f"
        axisTop={null}
        axisRight={null}
        axisBottom={{
          ...props.axisBottom,
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 45,
          legendOffset: 36,
          legendPosition: "middle",
          format: (e) => {
            if (isMobile && props.intervalValue === "DAILY") {
              return getFormatFilter(e, props.data);
            }
            return e;
          },
        }}
        axisLeft={{
          ...props.axisLeft,
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: "",
          legendOffset: -40,
          legendPosition: "middle",
          format: (e) => Math.floor(e) === e && e,
        }}
        colors={{
          scheme: "category10",
        }}
        lineWidth={3}
        tooltip={(tooltipProps) => <LineChartTooltip {...tooltipProps} />}
        enableGridX={false}
        enablePoints={props.data[0].data?.length <= 1 ? true : false}
        pointSize={10}
        pointColor={{ from: "color" }}
        pointLabelYOffset={-12}
        useMesh={true}
        layers={[
          "grid",
          "markers",
          "axes",
          "areas",
          "crosshair",
          "lines",
          "points",
          "slices",
          "mesh",
          BarLegend,
        ]}
        legends={[
          {
            //data: props.data.map((item:any)=> item),
            data: props.data.map((item: any, index: number) => ({
              color: getChartColorSchema()[index],
              id: item.id,
              label:
                item.id?.length > 16 ? item.id.slice(0, 15) + "..." : item.id,
            })),
            anchor: "top-right",
            direction: isMobile ? "column" : "row",
            justify: false,
            translateX: isMobile ? -30 : -100,
            translateY: isMobile ? -25 * props.data.length : -30,
            itemsSpacing: isMobile ? 5 : 20,
            itemDirection: "left-to-right",
            itemTextColor: color,
            itemWidth: 100,
            itemHeight: 20,
            itemOpacity: 1,
            symbolSize: 12,
            symbolShape: "square",
            symbolBorderColor: "rgba(0, 0, 0, .5)",
            effects: [
              {
                on: "hover",
                style: {
                  itemBackground: "rgba(0, 0, 0, .03)",
                },
              },
            ],
          },
        ]}
        theme={{
          crosshair: {
            line: {
              strokeWidth: 0.5,
              stroke: `${color}`,
            },
          },
          axis: {
            ticks: {
              line: {
                stroke: `${color}70`,
              },
              text: {
                // fill: COLORS.BUTTONS1,
                fill: `${color}70`,
                fontSize: isMobile ? calcRem(9) : calcRem(12),
              },
            },
          },
          grid: {
            line: {
              // stroke: COLORS.BORDER,
              stroke: `${color}30`,
              strokeWidth: 0.5,
            },
          },
        }}
      />
    </Wrapper>
  );
}

const Wrapper = styled.div`
  width: calc(100% - 50px);
  overflow: visible;
  svg {
    overflow: visible;
  }
  ${mediaQuery.isMobile} {
    width: calc(100% - 10px);
  }
`;
